import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Compass, ArrowRight, Shield, Eye } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

export const Welcome = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();

  const content = {
    en: {
      philosophy: [
        "IN is built on a simple premise: you can't navigate what you can't see.",
        "We often feel stuck, scattered, or unsure where to focus — not because we lack motivation, but because we lack a map. IN gives you that map, then helps you move.",
        "Inspired by Jungian psychology, narrative identity, and behavioral science, IN treats personal development not as self-improvement theater, but as real navigation — with a destination, a current position, and a path forward.",
      ],
      toBegin: "To build your free Sun Map, we need some information about you.",
      privacy: "Your answers are private, confidential, and used only to generate your personal map and guidance.",
      honesty: "The more honest and detailed your answers are, the more accurate your map becomes.",
      letsBegin: "Let's begIN.",
      time: 'About 5-10 minutes',
      button: 'Begin',
    },
    tr: {
      philosophy: [
        "IN basit bir önerme üzerine kurulu: göremediğin şeyde yol alamazsın.",
        "Çoğu zaman sıkışmış, dağınık veya nereye odaklanacağımızdan emin değiliz — motivasyon eksikliğinden değil, bir haritamız olmadığından. IN sana o haritayı verir, sonra hareket etmene yardımcı olur.",
        "Jung psikolojisi, anlatı kimliği ve davranış biliminden ilham alan IN, kişisel gelişimi kendini geliştirme tiyatrosu olarak değil, gerçek bir navigasyon olarak ele alır — bir hedef, mevcut bir konum ve ileriye dönük bir yol ile.",
      ],
      toBegin: "Ücretsiz Güneş Haritanı oluşturmak için senden bazı bilgilere ihtiyacımız var.",
      privacy: "Yanıtların gizlidir, özeldir ve yalnızca kişisel haritanı ve rehberliğini oluşturmak için kullanılır.",
      honesty: "Yanıtların ne kadar dürüst ve ayrıntılı olursa, haritan o kadar doğru olur.",
      letsBegin: "Hadi başlayalIN.",
      time: 'Yaklaşık 5-10 dakika',
      button: 'Başla',
    },
  };

  const c = content[language] || content.en;

  return (
    <div className="min-h-screen relative flex items-center justify-center px-6 py-16">
      <div className="max-w-2xl w-full">
        {/* Sun Icon */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8"
        >
          <div className="w-16 h-16 rounded-full bg-[#D4AF37]/20 flex items-center justify-center mx-auto border border-[#D4AF37]/30">
            <Compass className="w-8 h-8 text-[#D4AF37]" />
          </div>
        </motion.div>

        {/* Main Content Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="glass-panel rounded-2xl p-8 md:p-10"
        >
          {/* Philosophy paragraphs - centered */}
          <div className="space-y-5 font-sans text-[#E5E5E5] leading-relaxed text-center mb-8">
            {c.philosophy.map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
          </div>

          {/* Divider */}
          <div className="w-16 h-px bg-[#D4AF37]/40 mx-auto my-8" />

          {/* What we need - centered */}
          <p className="font-sans text-[#E5E5E5] leading-relaxed text-center mb-6">
            {c.toBegin}
          </p>

          {/* Privacy & Honesty with icons */}
          <div className="space-y-4 max-w-md mx-auto mb-8">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-[#D4AF37]/60 flex-shrink-0 mt-0.5" />
              <p className="font-sans text-sm text-[#A1A1AA] leading-relaxed">
                {c.privacy}
              </p>
            </div>
            <div className="flex items-start gap-3">
              <Eye className="w-5 h-5 text-[#D4AF37]/60 flex-shrink-0 mt-0.5" />
              <p className="font-sans text-sm text-[#A1A1AA] leading-relaxed">
                {c.honesty}
              </p>
            </div>
          </div>

          {/* Divider */}
          <div className="w-16 h-px bg-[#D4AF37]/40 mx-auto my-8" />

          {/* Let's begIN */}
          <p className="font-sans text-[#F8F5F2] text-lg text-center mb-2">
            {c.letsBegin}
          </p>

          {/* Time estimate */}
          <p className="text-[#6B7280] font-sans text-sm text-center mb-6">
            {c.time}
          </p>

          {/* Begin Button */}
          <div className="text-center">
            <button
              onClick={() => navigate('/intake')}
              className="inline-flex items-center gap-3 rounded-full border border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37] px-12 py-4 hover:bg-[#D4AF37] hover:text-[#030305] transition-all duration-300 shadow-[0_0_20px_rgba(212,175,55,0.15)] hover:shadow-[0_0_30px_rgba(212,175,55,0.4)] font-sans text-lg group"
              data-testid="begin-button"
            >
              {c.button}
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Welcome;
